import { Component, EventEmitter, Input, Output } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
    selector: 'app-map-tune-panel',
    templateUrl: './map-tune-panel.component.html',
    styleUrls: ['./map-tune-panel.component.scss'],
    animations: [
        trigger('slideInOut', [
            state('hidden', style({
                transform: 'translateX(100%)',
                display: 'none'
            })),
            state('visible', style({
                transform: 'translateX(0)',
                display: 'block'
            })),
            transition('hidden => visible', [
                style({ display: 'block' }),
                animate('200ms ease-in-out')
            ]),
            transition('visible => hidden', [
                animate('0ms ease-in-out', style({ transform: 'translateX(100%)' })),
                style({ display: 'none' })
            ])
        ])
    ]
})
export class MapTunePanelComponent {
    @Input() slideState: string = 'hidden';
    @Input() zIndex: number = 50;
    @Input() loadingMap: boolean = false;
    @Input() markers: any[] = [];
    @Input() hasBaseStationData: boolean = false;
    @Input() enableSiteMapping: boolean = false;

    @Input() noteIconScalePercent: number = 100;
    @Output() noteIconScalePercentChange = new EventEmitter<number>();

    @Input() carOfInterestIconScalePercent: number = 100;
    @Output() carOfInterestIconScalePercentChange = new EventEmitter<number>();

    @Input() annotationIconScalePercent: number = 100;
    @Output() annotationIconScalePercentChange = new EventEmitter<number>();

    @Input() personOfInterestIconScalePercent: number = 100;
    @Output() personOfInterestIconScalePercentChange = new EventEmitter<number>();

    @Input() locationIconScalePercent: number = 100;
    @Output() locationIconScalePercentChange = new EventEmitter<number>();

    @Input() threatIconScalePercent: number = 100;
    @Output() threatIconScalePercentChange = new EventEmitter<number>();

    @Input() cameraIconScalePercent: number = 100;
    @Output() cameraIconScalePercentChange = new EventEmitter<number>();

    @Input() investigatorIconScalePercent: number = 100;
    @Output() investigatorIconScalePercentChange = new EventEmitter<number>();

    @Input() investigatorGroupIconScalePercent: number = 100;
    @Output() investigatorGroupIconScalePercentChange = new EventEmitter<number>();

    @Input() customMarkerIconScalePercent: number = 100;
    @Output() customMarkerIconScalePercentChange = new EventEmitter<number>();

    @Input() buildingIconScalePercent: number = 100;
    @Output() buildingIconScalePercentChange = new EventEmitter<number>();
    @Input() cameraSectorOpacityPercent: number = 100;
    @Output() cameraSectorOpacityPercentChange = new EventEmitter<number>();
    @Input() baseStationSectorOpacityPercent: number = 100;
    @Output() baseStationSectorOpacityPercentChange = new EventEmitter<number>();
    @Input() allIconScalePercent: number = 100;
    @Output() allIconScalePercentChange = new EventEmitter<number>();
    private pendingAllIconScalePercent: number = 100;

    @Input() isTooltipOn: boolean = false;
    @Output() isTooltipOnChange = new EventEmitter<boolean>();
    @Input() isIconLabelsOn: boolean = true;
    @Output() isIconLabelsOnChange = new EventEmitter<boolean>();

    /** When false, building markers use effective scale 0% (hidden). */
    @Input() isBuildingIconsFilterOn: boolean = true;
    @Output() isBuildingIconsFilterOnChange = new EventEmitter<boolean>();

    @Input() isBaseStationFilterOn: boolean = false;
    @Output() isBaseStationFilterOnChange = new EventEmitter<boolean>();

    @Output() closed = new EventEmitter<void>();

    hasIconControls(): boolean {
        return this.hasNotes() ||
            this.hasCarOfInterest() ||
            this.hasAnnotations() ||
            this.hasPersonOfInterest() ||
            this.hasLocations() ||
            this.hasThreats() ||
            this.hasMarkerType('camera') ||
            this.hasMarkerType('building') ||
            this.hasMarkerType('investigator') ||
            this.hasMarkerType('investigator-group') ||
            this.hasCustomPins();
    }

    hasOpacityControls(): boolean {
        return this.hasMarkerType('camera') || this.hasBaseStationData;
    }

    hasNotes(): boolean {
        return this.activeMarkers().some((m) => {
            const t = this.markerType(m);
            if (t === 'case') {
                return true;
            }
            if (t !== 'note') {
                return false;
            }
            return !this.isCarOfInterest(m) && !this.isPersonOfInterest(m);
        });
    }

    hasCarOfInterest(): boolean {
        return this.activeMarkers().some((m) => this.isCarOfInterest(m));
    }

    hasPersonOfInterest(): boolean {
        return this.activeMarkers().some((m) => this.isPersonOfInterest(m));
    }

    hasAnnotations(): boolean {
        return this.hasMarkerType('annotation');
    }

    hasLocations(): boolean {
        return this.hasMarkerType('location');
    }

    hasThreats(): boolean {
        return this.hasMarkerType('threat');
    }

    hasCustomPins(): boolean {
        return this.hasMarkerType('squad') || this.hasMarkerType('teammember');
    }

    hasMarkerType(type: string): boolean {
        return this.activeMarkers().some((m) => this.markerType(m) === type);
    }

    onAllIconScalePreview(value: number): void {
        const v = this.clampScale(value);
        this.allIconScalePercent = v;
        this.pendingAllIconScalePercent = v;
        this.allIconScalePercentChange.emit(v);
    }

    applyAllIconScales(): void {
        const v = this.clampScale(this.pendingAllIconScalePercent);
        this.allIconScalePercent = v;
        this.allIconScalePercentChange.emit(v);
        this.noteIconScalePercentChange.emit(v);
        this.carOfInterestIconScalePercentChange.emit(v);
        this.annotationIconScalePercentChange.emit(v);
        this.personOfInterestIconScalePercentChange.emit(v);
        this.locationIconScalePercentChange.emit(v);
        this.threatIconScalePercentChange.emit(v);
        this.cameraIconScalePercentChange.emit(v);
        this.investigatorIconScalePercentChange.emit(v);
        this.investigatorGroupIconScalePercentChange.emit(v);
        this.customMarkerIconScalePercentChange.emit(v);
        this.buildingIconScalePercentChange.emit(v);
    }

    private clampScale(value: number): number {
        const n = Number(value);
        if (!Number.isFinite(n)) {
            return 100;
        }
        return Math.max(0, Math.min(100, Math.round(n / 5) * 5));
    }

    private activeMarkers(): any[] {
        if (!Array.isArray(this.markers)) {
            return [];
        }
        return this.markers.filter((m) => !!m && m.delete !== true);
    }

    private markerType(mark: any): string {
        return String(mark?.markerType || '').toLowerCase();
    }

    private isCarOfInterest(mark: any): boolean {
        return this.markerType(mark) === 'note' && (mark?.car_of_interest || mark?.note?.car_of_interest);
    }

    private isPersonOfInterest(mark: any): boolean {
        return this.markerType(mark) === 'note' && (mark?.person_of_interest || mark?.note?.person_of_interest);
    }
}
